import { useEffect } from 'react'
import { useWebSocketContext } from '@/hooks/useWebSocketContext'
import type { ConversationStatus } from '@/services/ConversationService'

// Define interfaces for WebSocket data
export interface MessageData {
  id: string
  text: string
  message?: string
  content?: string
  sender: string
  timestamp: string
  conversation_id: string
  message_count?: number
  // User/platform metadata from WebSocket
  user_display_name?: string
  source?: string
  platform?: string
  user?: string
}

export interface ConversationUpdateData {
  id: string
  status?: ConversationStatus
  ui_status?: ConversationStatus
  old_status?: ConversationStatus
  new_status?: ConversationStatus
  updated_at: string
  timestamp?: string
  title?: string
  conversation_title?: string
  description?: string
  metadata?: Record<string, unknown>
  intent_status?: string
  update?: string
  update_type?: string
  chatbot_enabled?: boolean
  chatbot_pause_end?: string
  chatbot_pause_type?: string
}

export interface UseContextWebSocketOptions {
  onNewMessage?: (conversationId?: string, messageData?: MessageData) => void
  onConversationUpdate?: (conversationId?: string, updateData?: ConversationUpdateData) => void
}

export interface UseContextWebSocketReturn {
  isConnected: boolean
  connectionState: 'connecting' | 'connected' | 'disconnected' | 'error'
}

export function useContextWebSocket(options: UseContextWebSocketOptions = {}): UseContextWebSocketReturn {
  const { onNewMessage, onConversationUpdate } = options
  const { isConnected, connectionState, subscribe } = useWebSocketContext()

  // Suscribirse a los eventos del WebSocket
  useEffect(() => {
    const unsubscribe = subscribe((conversationId?: string, messageData?: unknown, eventType?: string) => {
      // Cuando llega cualquier evento del WebSocket, ejecutar los callbacks con información específica
      if (eventType === 'new_message' && onNewMessage) {
        onNewMessage(conversationId, messageData as MessageData)
      }
      if (eventType === 'conversation_update' && onConversationUpdate) {
        onConversationUpdate(conversationId, messageData as ConversationUpdateData)
      }
    })

    return unsubscribe
  }, [subscribe, onNewMessage, onConversationUpdate])

  return {
    isConnected,
    connectionState
  }
}
